import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiConsumeLink, apiMe } from '../api';
import type { User } from '../types';

interface Props {
  onLoggedIn: (u: User) => void;
}

/**
 * Magic-link confirmation page.
 *
 * Why this exists: chat clients (Slack/Discord/LINE/…) auto-fetch URLs in
 * messages to render previews. If /verify consumed the token on GET, the
 * link would burn before the human clicked. So GET /verify just renders this
 * confirmation page; the actual consumption only fires when the human
 * clicks the button (which is a POST that crawlers don't follow).
 */
export default function Verify({ onLoggedIn }: Props) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    setErr(null);
    setBusy(true);
    try {
      const r = await apiConsumeLink(token);
      // Cookie is now set; refresh app state so the topbar shows the user
      // before we navigate.
      const me = await apiMe();
      if (me.user) onLoggedIn(me.user);
      navigate(r.redirect || '/label', { replace: true });
    } catch (e) {
      // api() throws with the server's `message` or `error` field as the
      // message. Map known codes to friendlier copy.
      const msg = String(e);
      if (msg.includes('link_already_used')) {
        setErr('此連結已被使用過,請向管理員索取新連結');
      } else if (msg.includes('invalid_or_expired')) {
        setErr('連結無效或已過期');
      } else {
        setErr(msg);
      }
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <h2>登入連結</h2>
          <div className="error">連結缺少 token,請向管理員索取新連結。</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h2>確認登入</h2>
        <p className="muted small">
          請點擊下方按鈕完成登入。為避免連結被通訊軟體預覽抓取而失效,
          需要由您手動確認。
        </p>
        <button className="primary" onClick={confirm} disabled={busy}>
          {busy ? '驗證中…' : '確認登入'}
        </button>
        {err && <div className="error">{err}</div>}
      </div>
    </div>
  );
}
